import { useTranslation } from "react-i18next"

import type { FixedCostCurrency } from "@/api/admin-api"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type LegacyCostField = "platformCost" | "infraCost" | "projectCost" | "seatBenchmark"

/** The legacy fixed-cost inputs, shown only while the legacy cost model is editable. */
export function LocalAiLeadLegacyCostFields({
  platformCost,
  infraCost,
  projectCost,
  seatBenchmark,
  currency,
  disabled,
  onChange,
  onCurrencyChange,
}: {
  platformCost: string | null
  infraCost: string | null
  projectCost: string | null
  seatBenchmark: string | null
  currency: FixedCostCurrency
  disabled: boolean
  onChange: (field: LegacyCostField, value: string) => void
  onCurrencyChange: (currency: FixedCostCurrency) => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="local-ai-lead-platform-cost">
            {t("adminDashboard.localAiLeadSettings.platformCostLabel", "Monthly platform cost")}
          </Label>
          <Input
            disabled={disabled}
            id="local-ai-lead-platform-cost"
            inputMode="decimal"
            min={0}
            step="0.01"
            type="number"
            value={platformCost ?? ""}
            onChange={(e) => onChange("platformCost", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="local-ai-lead-cost-currency">
            {t("adminDashboard.localAiLeadSettings.costCurrencyLabel", "Currency")}
          </Label>
          <Select
            disabled={disabled}
            value={currency}
            onValueChange={(v) => onCurrencyChange(v as FixedCostCurrency)}
          >
            <SelectTrigger className="w-28" id="local-ai-lead-cost-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="SEK">SEK</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.localAiLeadSettings.costHelp",
          "Combined fixed monthly cost (platform infrastructure + subscription), matching how the platform is invoiced. Divided across all globally active users to estimate each Local AI Lead's cost."
        )}
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="local-ai-lead-infra-cost">
            {t("adminDashboard.localAiLeadSettings.infraCostLabel", "Per-user infra cost")}
          </Label>
          <Input
            disabled={disabled}
            id="local-ai-lead-infra-cost"
            inputMode="decimal"
            min={0}
            step="0.01"
            type="number"
            value={infraCost ?? ""}
            onChange={(e) => onChange("infraCost", e.target.value)}
          />
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.localAiLeadSettings.infraCostHelp",
          "Flat monthly infra-cost assumption added per assigned user (e.g. their share of Azure infra), on top of their usage and platform-cost share. Not divided across global users."
        )}
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="local-ai-lead-project-cost">
            {t("adminDashboard.localAiLeadSettings.projectCostLabel", "Project work")}
          </Label>
          <Input
            disabled={disabled}
            id="local-ai-lead-project-cost"
            inputMode="decimal"
            min={0}
            step="0.01"
            type="number"
            value={projectCost ?? ""}
            onChange={(e) => onChange("projectCost", e.target.value)}
          />
        </div>
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="local-ai-lead-seat-benchmark">
            {t("adminDashboard.localAiLeadSettings.seatBenchmarkLabel", "Seat benchmark")}
          </Label>
          <Input
            disabled={disabled}
            id="local-ai-lead-seat-benchmark"
            inputMode="decimal"
            min={0}
            step="0.01"
            type="number"
            value={seatBenchmark ?? ""}
            onChange={(e) => onChange("seatBenchmark", e.target.value)}
          />
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.localAiLeadSettings.projectCostHelp",
          "Annual project work, spread over 12 months and divided across all active users together with the monthly platform cost. Zero leaves the cost unchanged. The seat benchmark is a reference line on the adoption curve and is not part of the cost."
        )}
      </p>
    </>
  )
}
